import { type ReactElement } from 'react'
import { getInitials, isEmpty, type DashboardWidget, type TabularResult } from '@terros/common'
import { Group, Text } from '@mantine/core'
import { getInitialism, getReportGroupEntityName, getUserColor, type ReportGroupEntity } from '@front/model'
import { LeaderboardDisplay, type LeaderboardDisplayRow } from './LeaderboardDisplay'
import { toLeaderboardRows, type LeaderboardRow } from './leaderboard'

type Props = {
  metricIndex: number
  widget: Extract<DashboardWidget, { type: 'leaderboard' }>
  data: TabularResult
}

const LEADERBOARD_ROW_LIMIT = 10

export function LeaderboardWidget({ data, metricIndex, widget }: Props): ReactElement {
  const rows = toLeaderboardRows({ data, metricIndex, widget })

  if (isEmpty(rows)) return <EmptyLeaderboard />

  return <LeaderboardDisplay rows={toDisplayRows(rows.slice(0, LEADERBOARD_ROW_LIMIT))} />
}

function EmptyLeaderboard(): ReactElement {
  return (
    <Group justify='center'>
      <Text c='dimmed' size='sm'>
        No data
      </Text>
    </Group>
  )
}

function toDisplayRows(rows: LeaderboardRow[]): LeaderboardDisplayRow[] {
  return rows.map((row, index) => ({
    rank: index + 1,
    name: getReportGroupEntityName(row.entity),
    value: formatValue(row.value),
    avatar: toAvatar(row.entity),
  }))
}

function toAvatar(entity: ReportGroupEntity): LeaderboardDisplayRow['avatar'] {
  if ('userId' in entity) {
    return {
      avatarBlurhash: entity.avatarBlurhash,
      avatarUrl: entity.avatarUrl,
      color: getUserColor(entity),
      initials: getInitials(entity),
    }
  }
  return {
    avatarBlurhash: entity.avatarBlurhash,
    avatarUrl: entity.avatarUrl,
    initials: getInitialism(entity.name),
  }
}

function formatValue(value: number | null): string {
  if (value === null) return '-'
  return value.toLocaleString()
}
